import { ApolloClient } from '@apollo/client/core/index.js'
import { InMemoryCache } from '@apollo/client/cache/index.js'
import { ApolloLink } from '@apollo/client/link/index.js'
import { HttpLink } from '@apollo/client/link/http/index.js'
import { PersistedQueryLink } from '@apollo/client/link/persisted-queries/index.js'
import { apolloLogger, createMyApolloLink } from 'nuxt-base/apollo/apollo'

export default defineNuxtPlugin({
  parallel: true,
  setup(nuxtApp) {
    const config = useRuntimeConfig()
    const DEV_BACKEND_URL = () => (!!import.meta.server && config.backendUrlPrivate) || config.public.backendUrlPublic
    const lang = useLocale()
    const getCsrfCookie = useRawCookies('xcsrftoken')

    function createLinkBase(inp: { useGETForHashedQueries?: boolean; link: ApolloLink }): ApolloLink {
      return ApolloLink.from([
        // new RetryLink(),
        // ...(import.meta.dev ? [apolloLogger()] : []),
        apolloLogger(),

        createMyApolloLink({
          lang: () => lang.value,
          csrfCookie: () => getCsrfCookie.get(),
        }),

        new PersistedQueryLink({
          useGETForHashedQueries: inp.useGETForHashedQueries,
          generateHash: (i) => {
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            return (i as any)?.__meta__?.hash
          },
        }),

        inp.link,
      ])
    }

    const apolloFixMerge = { merge: (_: unknown, incoming: unknown) => incoming }

    function createCache(type: 'public' | 'private') {
      const cacheKey = '_apollo:cache:' + type

      const cache = new InMemoryCache({
        typePolicies: {
          UpcomingEntry: { keyFields: ['productId'] },
          User: { keyFields: (object) => (object?.isMe === true ? [] : ['_id']) },
          Cart: { keyFields: (object) => (object?.isMe === true ? [] : ['_id']) },
          Me: { keyFields: [] },
          OrderDraft: { keyFields: [], fields: { optionContacts: apolloFixMerge, optionAddress: apolloFixMerge } },
          BuyerProductBoard: { fields: { images: apolloFixMerge } },
          BuyerCart: { keyFields: [], fields: { items: apolloFixMerge, discounts: apolloFixMerge } },
          UserDoc: { keyFields: [] },
          Store: { keyFields: [] },
          CartProduct: { keyFields: false },
          SearchCatalogFiltersSeller: { keyFields: false, merge: false },
          CheckoutDraftDeliveryItem: { keyFields: false },
          ImageNodeItem: { keyFields: false },
        },
      })

      useHydration(
        cacheKey,
        () => cache.extract(),
        (i) => {
          if (!i) return
          cache.restore(i)
          nuxtApp.payload[cacheKey] = undefined
        }
      )
      return cache
    }

    const apolloClientGet = new ApolloClient({
      // Set this on the server to optimize queries when SSR
      // This will temporary disable query force-fetching
      ...(import.meta.server ? { ssrMode: true } : { ssrForceFetchDelay: 100 }),
      defaultOptions: { query: { errorPolicy: 'all' }, mutate: { errorPolicy: 'all' }, watchQuery: { errorPolicy: 'all' } },
      cache: createCache('public'),
      link: createLinkBase({
        useGETForHashedQueries: true,
        link: new HttpLink({
          credentials: 'include',
          uri: () => {
            const url = new URL(DEV_BACKEND_URL())
            url.searchParams.set('lang', lang.value) // Append lang query parameter
            return url.toString()
          },
        }),
      }),
    })

    const apollo = {
      default: apolloClientGet,
      get: apolloClientGet,
      post: new ApolloClient({
        ssrMode: false,
        defaultOptions: {
          query: { errorPolicy: 'all', fetchPolicy: import.meta.server ? 'cache-only' : 'cache-first' },
          mutate: { errorPolicy: 'all' },
          watchQuery: { errorPolicy: 'all' },
        },
        cache: createCache('private'),
        link: createLinkBase({
          useGETForHashedQueries: false,
          // link: new BatchHttpLink({ uri: DEV_BACKEND_URL(), credentials: 'include' }),
          link: new HttpLink({ uri: DEV_BACKEND_URL(), credentials: 'include', useGETForQueries: false }),
        }),
      }),
    } satisfies Record<string, ApolloClient>

    if (import.meta.env.DEV) {
      // Adds messages only in a dev environment
      import('@apollo/client/dev').then(({ loadDevMessages, loadErrorMessages }) => {
        loadDevMessages()
        loadErrorMessages()
      })
    }

    return { provide: { apollo } }
  },
})
